import { type OmitIndexSignature } from 'type-fest';

import {
  type ElementMetaData,
  type ElementMetaUiFeaturesProps,
  isContainerElementMetaUiFeatures,
  isInputElementMetaUiFeatures,
} from '@blaze-shared/elements-domain';
import { deepmergeExceptArrays } from '@blaze-shared/shared-util';

import { type CanvasElementType } from './canvas-element-id';
import { type ElementMetaPaths } from './element-taxonomy';
import { type PartialPropsOf } from './props';
/** Strictly typed props patching mechanism */
export function applyPropsPatch<T extends CanvasElementType = CanvasElementType>(
  props: PartialPropsOf<T>,
  update: OmitIndexSignature<PartialPropsOf<T>>,
): PartialPropsOf<T> {
  return deepmergeExceptArrays(props, update) as PartialPropsOf<T>;
}

export const resolveElementMetaDisplayValue = (
  path: string,
  property: ElementMetaUiFeaturesProps | 'title' | 'titleWidth',
  meta: ElementMetaData,
  paths: ElementMetaPaths | undefined,
): string | undefined => {
  if (!meta.uiFeatures) {
    return undefined;
  }
  if (isContainerElementMetaUiFeatures(meta.uiFeatures)) {
    const colIdx = /\.(\d+)\.?/.exec(path)?.[1];
    if (colIdx !== undefined) {
      let columns = meta.uiFeatures?.columns;
      if (typeof paths?.columns === 'object') {
        const type = Object.entries(paths.columns).find(([, pathPrefix]) =>
          path.startsWith(pathPrefix),
        )?.[0];
        columns = columns?.filter(({ fieldType }) => fieldType === type);
      }
      return columns?.[+colIdx]?.[
        property !== 'title' && property !== 'titleWidth' ? property : 'name'
      ] as any;
    }
    if (property === 'title') {
      return meta.uiFeatures.title;
    }
    return undefined;
  } else if (isInputElementMetaUiFeatures(meta.uiFeatures)) {
    return meta.uiFeatures?.[property] as any;
  }
  throw new Error('Unrecognized feature type');
};
